import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon, Route as RouteIcon } from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';
import { formatDistance, formatDuration, getRouteColor } from '../../constants/routePlanning';

/**
 * Step 3 for Auto mode: assign a rider to each already-created route. Reuses the same rider
 * search and the same assignment calls (bulk-update-orders + route PUT) as the single-route flow,
 * looped per route. Routes left without a rider stay unallocated and can be assigned later.
 */
const MultiRiderAssign = ({ createdRoutes = [], failedRoutes = [], onDone }) => {
  const { actions } = useApp();
  const [searchByRoute, setSearchByRoute] = useState({});
  const [searchingId, setSearchingId] = useState(null);
  const [optionsByRoute, setOptionsByRoute] = useState({});
  const [assignments, setAssignments] = useState({}); // routeId -> rider
  const [statusById, setStatusById] = useState({}); // routeId -> 'done' | 'error'
  const [assigning, setAssigning] = useState(false);

  const token = () => localStorage.getItem(appConfig.storage.token);

  const takenRiderIds = (exceptRouteId) =>
    new Set(
      Object.entries(assignments)
        .filter(([routeId, rider]) => routeId !== exceptRouteId && rider?.userId)
        .map(([, rider]) => rider.userId)
    );

  const handleSearch = async (routeId) => {
    const term = (searchByRoute[routeId] || '').trim();
    if (!term) {
      actions.showSnackbar('Enter name or mobile number to search', 'warning');
      return;
    }
    setSearchingId(routeId);
    try {
      const response = await fetch(
        `${appConfig.api.baseURL}/users/riders/list?search=${encodeURIComponent(term)}`,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      if (!response.ok) throw new Error('Failed to fetch riders');
      const data = await response.json();
      if (data.success) {
        setOptionsByRoute((prev) => ({ ...prev, [routeId]: data.riders || [] }));
        if (!(data.riders || []).length) actions.showSnackbar('No riders found', 'info');
      } else {
        actions.showSnackbar(data.message || 'Search failed', 'error');
      }
    } catch (err) {
      actions.showSnackbar(err.message || 'Failed to search riders', 'error');
    } finally {
      setSearchingId(null);
    }
  };

  const assignOne = async (route, rider) => {
    const orderRes = await fetch(`${appConfig.api.baseURL}/orders/bulk-update-orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({
        orderIds: route.orderedIds,
        updateData: {
          deliveryPersonMobile: rider.userId,
          deliveryPersonName: rider.f_name,
          deliveryFrom: 'PILOT',
          routeId: route.routeId,
          order_status: 'assigned',
        },
      }),
    });
    if (!orderRes.ok) throw new Error(`HTTP ${orderRes.status}`);
    const orderData = await orderRes.json();
    if (!orderData.success) throw new Error(orderData.message || 'Assign failed');

    try {
      await fetch(`${appConfig.api.baseURL}/delivery-routes/${route.routeId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          updateData: {
            deliveryPersonId: rider.userId,
            deliveryPersonName: rider.f_name,
            deliveryPersonMobile: rider.mobileNo,
          },
        }),
      });
    } catch (routeErr) {
      console.warn('Route PUT failed (order assignment still saved):', routeErr);
    }
  };

  const handleAssignAll = async () => {
    const pending = createdRoutes.filter(
      (route) => assignments[route.routeId] && statusById[route.routeId] !== 'done'
    );
    if (pending.length === 0) {
      actions.showSnackbar('Pick a rider for at least one route', 'warning');
      return;
    }
    setAssigning(true);
    const nextStatus = { ...statusById };
    let ok = 0;
    for (const route of pending) {
      try {
        await assignOne(route, assignments[route.routeId]);
        nextStatus[route.routeId] = 'done';
        ok += 1;
      } catch (err) {
        nextStatus[route.routeId] = 'error';
      }
    }
    setStatusById(nextStatus);
    setAssigning(false);
    actions.showSnackbar(`Assigned ${ok} of ${pending.length} route(s).`, ok ? 'success' : 'error');
  };

  const allDone =
    createdRoutes.length > 0 &&
    createdRoutes.every((route) => statusById[route.routeId] === 'done');

  return (
    <Box>
      {failedRoutes.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {failedRoutes.length} route(s) failed to create and are not shown here. Go back and try
          creating them again.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search inside each route and pick that route&apos;s rider. A rider can be on one route
        only. Leave a route blank to assign it later from the Routes screen.
      </Typography>

      {createdRoutes.map((route, index) => {
        const color = getRouteColor(route.colorIndex ?? index).hex;
        const status = statusById[route.routeId];
        const searching = searchingId === route.routeId;
        const taken = takenRiderIds(route.routeId);
        const options = (optionsByRoute[route.routeId] || []).filter(
          (rider) => !taken.has(rider.userId) || assignments[route.routeId]?.userId === rider.userId
        );
        return (
          <Paper key={route.routeId} variant="outlined" sx={{ p: 1.5, mb: 1.5, borderColor: color }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <RouteIcon sx={{ color }} fontSize="small" />
              <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 700 }}>
                Route {index + 1} · {route.routeId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {route.orderedIds.length} stops · {formatDistance(route.totalDistanceMeters)} ·{' '}
                {formatDuration(route.totalDurationSeconds)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Rider for this route only
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Name or mobile for this route"
                value={searchByRoute[route.routeId] || ''}
                onChange={(e) =>
                  setSearchByRoute((prev) => ({ ...prev, [route.routeId]: e.target.value }))
                }
                onKeyDown={(e) => e.key === 'Enter' && handleSearch(route.routeId)}
                disabled={searching || assigning || status === 'done'}
              />
              <Button
                variant="outlined"
                onClick={() => handleSearch(route.routeId)}
                disabled={searching || assigning || status === 'done'}
                startIcon={searching ? <CircularProgress size={18} /> : <SearchIcon />}
              >
                Search
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Rider"
                value={assignments[route.routeId]?.userId || ''}
                onChange={(e) => {
                  const rider = options.find((r) => r.userId === e.target.value);
                  setAssignments((prev) => ({ ...prev, [route.routeId]: rider }));
                }}
                disabled={assigning || status === 'done' || options.length === 0}
                helperText={
                  options.length === 0
                    ? 'Search this route to load riders. Riders already picked on another route are hidden.'
                    : 'This rider is assigned only to this route.'
                }
              >
                {options.map((rider) => (
                  <MenuItem key={rider.userId} value={rider.userId}>
                    {rider.f_name} {rider.l_name || ''} — {rider.mobileNo}
                  </MenuItem>
                ))}
              </TextField>
              {status === 'done' && <Chip label="Assigned" color="success" size="small" />}
              {status === 'error' && <Chip label="Failed" color="error" size="small" />}
            </Box>
          </Paper>
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={onDone} disabled={assigning}>
          {allDone ? 'Done' : 'Skip / assign later'}
        </Button>
        <Button
          variant="contained"
          onClick={handleAssignAll}
          disabled={assigning || allDone}
          startIcon={assigning ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {assigning ? 'Assigning…' : 'Assign riders'}
        </Button>
      </Box>
    </Box>
  );
};

export default MultiRiderAssign;
